<template>
    <div v-if="loadingProjectInformation">Loading...</div>
    <VJoyPanel v-else-if="projectInformation">
        <template #panel-title>{{ projectInformation.contract.subject }}</template>
        <template #panel-subtitle>{{ t('projectclient.projectinformation.started.on') }} {{ startDay }}</template>

        <template #panel-body>
            <div class="project-details">
                <hr />
                <div class="project-details__avatar-section">
                    <VJoyAvatar :photo-url="projectInformation.freelancer.pictureUrl" />
                    <div class="freelancer-info">
                        <p>{{ projectInformation.freelancer.fullName }}</p>
                        <span>{{ projectInformation.freelancer.headline }}</span>
                    </div>
                </div>
                <hr />
                <h2 class="project-details__header">{{ t('projectclient.projectinformation.price.details') }}</h2>
                <PriceDetails
                    data-testid="project-details-price-details-total-amount-without-fees-excltax"
                    :label="t('projectclient.projectinformation.price.subtotal.excltax')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.priceOutOfTaxes, projectInformation.contract.currency, locale)"
                />
                <PriceDetails
                    data-testid="project-details-price-details-total-fees-excltax"
                    :label="t('projectclient.projectinformation.price.fees.excltax')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.feesOutOfTaxes, projectInformation.contract.currency, locale)"
                />
                <PriceDetails
                    data-testid="project-details-price-details-total-amount-excltax"
                    :bold="true"
                    :label="t('projectclient.projectinformation.price.total.excltax')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.subTotalOutOfTaxes, projectInformation.contract.currency, locale)"
                />
                <PriceDetails
                    data-testid="project-details-price-details-total-taxes"
                    :label="t('projectclient.projectinformation.price.taxes')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.taxes, projectInformation.contract.currency, locale)"
                />
                <PriceDetails
                    data-testid="project-details-price-details-total-amount-incltax"
                    :bold="true"
                    :total="true"
                    :label="t('projectclient.projectinformation.price.total.incltax')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.totalIncludingTaxes, projectInformation.contract.currency, locale)"
                />
                <PriceDetails
                    v-if="projectInformation.lot.invoiceDetails.alreadyPaidAmount"
                    data-testid="project-details-price-details-total-amount-incltax"
                    :label="t('projectclient.projectinformation.already.paid.amount')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.alreadyPaidAmount, projectInformation.contract.currency, locale)"
                />
                <PriceDetails
                    v-if="projectInformation.lot.invoiceDetails.alreadyPaidAmount"
                    data-testid="project-details-price-details-total-due-amount-incltax"
                    :bold="true"
                    :total="true"
                    :label="t('projectclient.projectinformation.price.amount.incltax')"
                    :value="handleCurrency(projectInformation.lot.invoiceDetails.dueAmount!!, projectInformation.contract.currency, locale)"
                />

                <VJoyHighlight
                    v-if="
                        projectInformation.lot.invoiceDetails.alreadyPaidAmount &&
                        projectInformation.lot.invoiceDetails.alreadyPaidAmount > projectInformation.lot.invoiceDetails.totalIncludingTaxes
                    "
                    level="info"
                    display-icon
                >
                    {{ t('projectclient.projectinformation.prepayment.refund.expected') }}
                </VJoyHighlight>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore} from '@common/stores';
    import {handleCurrency} from '@common/utils';
    import {useLocale, useTranslation} from '#imports';
    import {VJoyAvatar, VJoyHighlight, VJoyPanel} from '@maltjoy/core-vue';
    import PriceDetails from './PriceDetails.vue';
    import {useGetFullFormattedDate} from '../../utils';

    const {locale} = useLocale();

    const {t} = useTranslation();
    const {projectInformation, loadingProjectInformation} = useProjectInformationStore();

    const startDay = computed(() => {
        if (projectInformation?.contract?.startDate) {
            return useGetFullFormattedDate(projectInformation.contract.startDate);
        }
        return '';
    });
</script>

<style lang="scss">
    .project-details {
        hr {
            height: 1px;
            background-color: var(--joy-color-neutral-30);
            border: none;
            margin-bottom: var(--joy-core-spacing-4);
        }

        &__header {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-450);
            line-height: var(--joy-line-height-large);
        }

        &__input {
            font-size: var(--joy-font-size-primary-300) !important;
        }

        &__button {
            flex: 1;
            margin-bottom: 0;
            padding: 0 !important;
            font-size: var(--joy-font-size-primary-300) !important;
        }

        &__avatar-section {
            display: flex;
            align-items: center;
            margin-bottom: var(--joy-core-spacing-4);

            .freelancer-info {
                margin-left: var(--joy-core-spacing-2);

                & > p {
                    margin-bottom: var(--joy-core-spacing-0);
                    font-size: var(--joy-font-size-primary-400);
                    font-weight: var(--joy-font-weight-bold);
                }
            }
        }
    }
</style>
